import {
  Component,
  OnInit,
  Inject,
  OnDestroy,
  ChangeDetectorRef,
} from "@angular/core";
import {
  FormArray,
  FormControl,
  FormGroup,
  Validators,
  UntypedFormGroup,
  UntypedFormControl,
  AbstractControl,
} from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Store } from "@ngrx/store";
import {
  Branch,
  BranchVariable,
  BranchVariableFormDialogData,
  BranchVariableFormDialogResult,
  BranchVariableOption,
  DialogEmployee,
  VariableFormControlValue,
} from "../branch-settings.interface";
import { BranchSettingsService } from "../branch-settings.service";
import { EmployeesService } from "../../employees/employees.service";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { BranchVariableFormDialogComponent } from "../branch-variable-form-dialog/branch-variable-form-dialog.component";
import { MatTableDataSource } from "@angular/material/table";
import { StartCheckingForInstallationInProgressAction } from "../../signature-installation-state/signature-installation-state.actions";
import { ImagesService } from "../../media/images.service";
import type { Image } from "../../media/media.interface";
import type { HardcodedVariableIcon } from "../../digital-cards/digital-cards.interface";
import {
  HARDCODED_VARIABLE_ICONS,
  hardcodedIconByValue,
  hardcodedIconForVariable,
} from "../../digital-cards/variable-icons";
import {
  BRANCH_COUNTRY_VARIABLE_NAME,
  isPredefinedBranchVariable,
  PREDEFINED_BRANCH_VARIABLE_NAMES,
  PREDEFINED_BRANCH_VARIABLES,
} from "../branch-predefined-variables";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";
import { mediaFilePreview } from "../../shared/utils/media-file-preview.util";

@Component({
  selector: "app-branch-edit-dialog",
  templateUrl: "./branch-edit-dialog.component.html",
  styleUrls: ["./branch-edit-dialog.component.scss"],
  standalone: false,
})
export class BranchEditDialogComponent implements OnInit, OnDestroy {
  public sendingRequest = false;
  public form: UntypedFormGroup;
  public isEditMode: boolean;
  public branch: Branch | null;
  public errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();

  public predefinedVariables = [...PREDEFINED_BRANCH_VARIABLES];
  private readonly initialPredefinedVariableNames = [
    ...PREDEFINED_BRANCH_VARIABLE_NAMES,
  ];
  public selectedPredefinedVariables: BranchVariableOption[] = [];
  public selectedVariables: string[] = [];
  public customVariables: BranchVariable[] = [];
  public variablesDataSource = new MatTableDataSource<AbstractControl>();
  public variableDisplayedColumns: string[] = [
    "customField",
    "customLabel",
    "defaultValue",
    "icon",
    "mandatory",
    "overridable",
    "actions",
  ];
  public mediaImages: Image[] = [];
  public mediaImagesLoading = false;
  public hardcodedIcons: HardcodedVariableIcon[] = HARDCODED_VARIABLE_ICONS;
  readonly mediaFilePreview = mediaFilePreview;

  public allEmployees: DialogEmployee[] = [];
  public filteredEmployees: DialogEmployee[] = [];
  public selectedEmployees: DialogEmployee[] = [];
  public searchControl = new FormControl("");
  public isLoadingEmployees = false;
  public isLoadingVariables = false;
  private unsubscribe$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<BranchEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Branch | null,
    private service: BranchSettingsService,
    private employeesService: EmployeesService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private store: Store,
    private imagesService: ImagesService,
  ) {
    this.isEditMode = !!(data && data.branchId);
    this.branch = data;
    this.initializeForm();
    this.dialogRef.updateSize("90vw", "90vh");
  }

  ngOnInit(): void {
    if (this.isEditMode && this.branch) {
      this.loadBranchData();
    } else {
      this.selectedVariables = [...this.initialPredefinedVariableNames];
      this.selectedPredefinedVariables = [...this.predefinedVariables];
      this.updateVariablesForm();
    }

    this.loadEmployees();
    this.loadMediaImages();
    this.setupSearch();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private initializeForm(): void {
    this.form = new UntypedFormGroup({
      name: new UntypedFormControl(this.branch?.name || "", (c) =>
        Validators.required(c),
      ),
      variables: new FormArray([]),
      employeeIds: new UntypedFormControl([]),
    });
  }

  private loadBranchData(): void {
    if (!this.branch) {
      return;
    }

    const variableNames = Object.keys(this.branch.variables || {});

    this.predefinedVariables = [...PREDEFINED_BRANCH_VARIABLES];

    const customVarNames = variableNames.filter(
      (v) => !isPredefinedBranchVariable(v),
    );

    this.customVariables = customVarNames
      .map((v) => this.branch?.variables[v])
      .filter(Boolean);

    this.customVariables.forEach((customVar) => {
      this.predefinedVariables.push({
        name: customVar.name,
        label: customVar.label || customVar.name,
      });
    });

    const missingPredefined = this.initialPredefinedVariableNames.filter(
      (name) => !variableNames.includes(name),
    );
    this.selectedVariables = [...variableNames, ...missingPredefined];
    this.selectedPredefinedVariables = this.predefinedVariables.filter((p) =>
      this.selectedVariables.includes(p.name),
    );

    this.updateVariablesForm();
    this.form.markAsPristine();

    if (this.branch.employeeIds) {
      this.selectedEmployees = this.branch.employeeIds.map((employeeId) => ({
        employeeId,
        email: "",
      }));
    }
  }

  get variablesFormArray(): FormArray {
    return this.form.get("variables") as FormArray;
  }

  updateVariablesForm(): void {
    const variablesArray = this.variablesFormArray;
    const wasPristine = this.form.pristine;
    const currentValues = new Map(
      variablesArray.controls.map((control) => {
        const value = control.value as VariableFormControlValue;
        return [value.name, value];
      }),
    );
    variablesArray.clear();

    this.selectedVariables.forEach((varName) => {
      const customVar = this.customVariables.find((cv) => cv.name === varName);
      const existingVar = this.branch?.variables?.[varName];
      const currentValue = currentValues.get(varName);

      if (customVar) {
        variablesArray.push(
          new FormGroup({
            name: new FormControl(customVar.name),
            label: new FormControl(
              currentValue?.label ?? customVar.label ?? "",
            ),
            defaultValue: new FormControl(
              currentValue?.defaultValue ?? customVar.defaultValue ?? "",
            ),
            iconUrl: new FormControl(
              currentValue?.iconUrl?.trim() ||
                customVar.iconUrl?.trim() ||
                hardcodedIconForVariable(customVar.name).value,
            ),
            overridable: new FormControl(
              currentValue?.overridable ?? customVar.overridable !== false,
            ),
            mandatory: new FormControl(
              currentValue?.mandatory ?? customVar.mandatory ?? false,
            ),
          }),
        );
      } else {
        const predefinedVar = this.predefinedVariables.find(
          (variable) => variable.name === varName,
        );
        const predefinedDefaultValue = predefinedVar?.defaultValue ?? "";
        variablesArray.push(
          new FormGroup({
            name: new FormControl(varName),
            label: new FormControl(
              currentValue?.label ||
                existingVar?.label ||
                (predefinedVar
                  ? (this.translate.instant(predefinedVar.label) as string)
                  : ""),
            ),
            defaultValue: new FormControl(
              currentValue?.defaultValue ??
                existingVar?.defaultValue ??
                predefinedDefaultValue,
            ),
            iconUrl: new FormControl(
              currentValue?.iconUrl?.trim() ||
                existingVar?.iconUrl?.trim() ||
                hardcodedIconForVariable(varName).value,
            ),
            overridable: new FormControl(
              currentValue?.overridable ?? existingVar?.overridable !== false,
            ),
            mandatory: new FormControl(
              currentValue?.mandatory ?? existingVar?.mandatory ?? false,
            ),
          }),
        );
      }
    });

    this.variablesDataSource.data = variablesArray.controls;

    if (wasPristine) {
      this.form.markAsPristine();
    }

    this.cdr.detectChanges();
  }

  compareVariables(
    v1: BranchVariableOption,
    v2: BranchVariableOption,
  ): boolean {
    return v1 && v2 ? v1.name === v2.name : v1 === v2;
  }

  onChangePredefinedVariables(newVariables: BranchVariableOption[]): void {
    const selectedNames = newVariables.map((v) => v.name);
    const currentNames = this.selectedVariables;

    if (newVariables.length > currentNames.length) {
      newVariables.forEach((v) => {
        if (!currentNames.includes(v.name)) {
          this.selectedVariables.push(v.name);
        }
      });
    } else {
      this.selectedVariables = this.selectedVariables.filter((name) =>
        selectedNames.includes(name),
      );
    }

    this.selectedPredefinedVariables = newVariables;
    this.updateVariablesForm();
    this.form.markAsDirty();
  }

  addCustomVariable(): void {
    const existingFieldNames = [
      ...this.selectedVariables,
      ...this.customVariables.map((variable) => variable.name),
    ];

    const dialogRef = this.dialog.open<
      BranchVariableFormDialogComponent,
      BranchVariableFormDialogData,
      BranchVariableFormDialogResult | undefined
    >(BranchVariableFormDialogComponent, {
      width: "500px",
      panelClass: "app-dialog-panel",
      data: {
        existingFieldNames,
        predefinedVariables: this.predefinedVariables.map((variable) => ({
          ...variable,
          label: this.translate.instant(variable.label) as string,
        })),
        mediaImages: this.mediaImages,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: BranchVariableFormDialogResult | undefined) => {
        if (result && typeof result === "object" && "name" in result) {
          const name = result.name;
          const trimmedName = name.trim();
          if (!trimmedName) {
            return;
          }

          const allVariableNames = [
            ...this.selectedVariables,
            ...this.customVariables.map((v) => v.name),
          ];

          if (allVariableNames.includes(trimmedName)) {
            this.toastr.error(
              this.translate.instant("branchSettings.variableExists") as string,
            );
            return;
          }

          const newCustomVariable: BranchVariable = {
            name: trimmedName,
            label: result.label ? result.label.trim() : "",
            defaultValue: result.defaultValue,
            iconUrl: result.iconUrl,
            overridable: result.overridable,
            mandatory: result.mandatory,
          };

          this.customVariables.push(newCustomVariable);

          const customVariableOption = {
            name: trimmedName,
            label: result.label ? result.label.trim() : trimmedName,
          };
          if (
            !this.predefinedVariables.some(
              (variable) => variable.name === trimmedName,
            )
          ) {
            this.predefinedVariables.push(customVariableOption);
          }

          this.selectedVariables.push(trimmedName);
          const selectedOption =
            this.predefinedVariables.find(
              (variable) => variable.name === trimmedName,
            ) ?? customVariableOption;
          this.selectedPredefinedVariables.push(selectedOption);

          this.updateVariablesForm();
          this.form.markAsDirty();
        }
      });
  }

  removeCustomVariable(index: number): void {
    const removedVar = this.customVariables[index];
    this.customVariables.splice(index, 1);

    const predefinedIndex = this.predefinedVariables.findIndex(
      (p) => p.name === removedVar.name,
    );
    if (predefinedIndex >= 0) {
      this.predefinedVariables.splice(predefinedIndex, 1);
    }

    this.selectedVariables = this.selectedVariables.filter(
      (v) => v !== removedVar.name,
    );
    this.selectedPredefinedVariables = this.selectedPredefinedVariables.filter(
      (v) => v.name !== removedVar.name,
    );

    this.updateVariablesForm();
    this.form.markAsDirty();
  }

  isPredefinedVariable(name: string): boolean {
    return isPredefinedBranchVariable(name);
  }

  isBranchCountryVariable(name: string): boolean {
    return name === BRANCH_COUNTRY_VARIABLE_NAME;
  }

  removeVariable(index: number): void {
    const control = this.variablesFormArray.at(index);
    const name = String(control?.get("name")?.value ?? "").trim();
    if (!name || this.isPredefinedVariable(name)) return;

    const customIndex = this.customVariables.findIndex(
      (variable) => variable.name === name,
    );
    if (customIndex >= 0) {
      this.customVariables.splice(customIndex, 1);
      const predefinedIndex = this.predefinedVariables.findIndex(
        (variable) => variable.name === name,
      );
      if (predefinedIndex >= 0) {
        this.predefinedVariables.splice(predefinedIndex, 1);
      }
    }

    this.selectedVariables = this.selectedVariables.filter(
      (variableName) => variableName !== name,
    );
    this.selectedPredefinedVariables = this.selectedPredefinedVariables.filter(
      (variable) => variable.name !== name,
    );
    this.updateVariablesForm();
    this.form.markAsDirty();
  }

  iconSrc(url: string | null | undefined): string {
    if (!url) {
      return "";
    }
    return hardcodedIconByValue(url)?.preview ?? url;
  }

  iconLabel(url: string | null | undefined): string {
    if (!url) {
      return "";
    }
    const hardcoded = hardcodedIconByValue(url);
    if (hardcoded) {
      return hardcoded.label;
    }
    return (
      this.mediaImages.find((image) => image.imageUrl === url)?.name?.trim() ||
      url
    );
  }

  isUnknownIconUrl(url: string | null | undefined): boolean {
    if (!url?.trim()) {
      return false;
    }
    if (hardcodedIconByValue(url)) {
      return false;
    }
    return !this.mediaImages.some((image) => image.imageUrl === url);
  }

  private loadEmployees(): void {
    this.isLoadingEmployees = true;
    this.employeesService.getNotExcludedEmployeesWithAliases().subscribe({
      next: (data) => {
        this.allEmployees = data
          .filter((employee) => Boolean(employee.employeeId))
          .map((employee): DialogEmployee => ({
            employeeId: employee.employeeId,
            email: employee.email || "",
            fullName: employee.fullName,
            isAlias: false,
          }));
        this.selectedEmployees = this.selectedEmployees
          .map(
            (selected) =>
              this.allEmployees.find(
                (employee) => employee.employeeId === selected.employeeId,
              ) ?? selected,
          )
          .filter((employee) => Boolean(employee.employeeId));
        this.filterEmployees();
        this.isLoadingEmployees = false;
      },
      error: () => {
        this.isLoadingEmployees = false;
      },
    });
  }

  private loadMediaImages(): void {
    this.mediaImagesLoading = true;
    this.imagesService.getImages().subscribe({
      next: (list) => {
        this.mediaImages = Array.isArray(list) ? list : [];
        this.mediaImagesLoading = false;
      },
      error: () => {
        this.mediaImages = [];
        this.mediaImagesLoading = false;
      },
    });
  }

  private setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(() => this.filterEmployees());
  }

  private filterEmployees(): void {
    const searchValue = this.searchControl.value?.toLowerCase() || "";

    if (!searchValue) {
      this.filteredEmployees = [...this.allEmployees];
      return;
    }

    this.filteredEmployees = this.allEmployees.filter((emp) => {
      return (
        emp.fullName?.toLowerCase().includes(searchValue) ||
        emp.email?.toLowerCase().includes(searchValue)
      );
    });
  }

  toggleEmployeeSelection(employee: DialogEmployee): void {
    const index = this.selectedEmployees.findIndex(
      (e) => e.employeeId === employee.employeeId,
    );
    if (index === -1) {
      this.selectedEmployees.push(employee);
    } else {
      this.selectedEmployees.splice(index, 1);
    }
    this.form.markAsDirty();
  }

  isEmployeeSelected(employee: DialogEmployee): boolean {
    return this.selectedEmployees.some(
      (e) => e.employeeId === employee.employeeId,
    );
  }

  isAllSelected(): boolean {
    return (
      this.filteredEmployees.length > 0 &&
      this.filteredEmployees.every((emp) => this.isEmployeeSelected(emp))
    );
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.filteredEmployees.forEach((emp) => {
        const index = this.selectedEmployees.findIndex(
          (e) => e.employeeId === emp.employeeId,
        );
        if (index !== -1) {
          this.selectedEmployees.splice(index, 1);
        }
      });
    } else {
      this.filteredEmployees.forEach((emp) => {
        if (!this.isEmployeeSelected(emp)) {
          this.selectedEmployees.push(emp);
        }
      });
    }
    this.form.markAsDirty();
  }

  submit(): void {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.invalid) {
      return;
    }

    this.sendingRequest = true;

    const variables: Record<string, BranchVariable> = {};
    this.variablesFormArray.controls.forEach((control) => {
      const varData = control.value as VariableFormControlValue;
      variables[varData.name] = {
        name: varData.name,
        label: varData.label || null,
        defaultValue: varData.defaultValue || null,
        iconUrl: varData.iconUrl || null,
        overridable: varData.overridable,
        mandatory: varData.mandatory,
      };
    });

    const employeeIds = this.selectedEmployees
      .map((e) => e.employeeId)
      .filter((employeeId): employeeId is string => Boolean(employeeId));

    const formName = this.form.get("name")?.value as string | undefined;
    const branchData = {
      name: formName || "",
      variables,
      employeeIds,
    };

    if (this.isEditMode && this.branch && !this.branch.branchId) {
      this.sendingRequest = false;
      return;
    }

    const request =
      this.isEditMode && this.branch?.branchId
        ? this.service.updateBranch(this.branch.branchId, branchData)
        : this.service.addBranch(branchData);

    request.subscribe(
      () => {
        if (this.selectedEmployees.length > 0) {
          this.store.dispatch(
            new StartCheckingForInstallationInProgressAction(),
          );
        }
        this.dialogRef.close(true);
      },
      () => {
        this.sendingRequest = false;
      },
    );
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
